import { useMemo } from "react";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { bySize, riskMetrics, tradeScatter } from "../../lib/analytics";
import { byDay, closedOnly, equityCurve, streaks, summarize } from "../../lib/stats";
import { BucketTable, Insights, MetricGrid } from "../../ui/analytics";
import { PnlBars, TradeScatter, UnderwaterChart } from "../../ui/Charts";
import { Card } from "../../ui/ui";
import { compact, insight, type TabProps } from "./shared";

// Tranches de 250 $ pour la distribution des journées, bornées à ±750 $.
const STEP = 250;

export function RiskTab({ trades }: TabProps) {
  const tl = useTranslation().lite;
  const t = tl.stats.risk;
  const { fmt } = useLiteData();

  const r = riskMetrics(trades);
  const st = streaks(trades);
  const s = summarize(trades);
  const curve = useMemo(() => equityCurve(trades, fmt.locale), [trades, fmt.locale]);
  const scatter = useMemo(() => tradeScatter(trades), [trades]);
  const sizes = bySize(trades).map(b => ({ ...b, label: tl.stats.buckets.size[b.key] }));

  const dailyDist = useMemo(() => {
    const counts = new Map<number, number>();
    for (const d of byDay(closedOnly(trades))) {
      const b = Math.max(-4, Math.min(3, Math.floor(d.pnl / STEP)));
      counts.set(b, (counts.get(b) ?? 0) + 1);
    }
    return Array.from({ length: 8 }, (_, i) => i - 4).map(b => ({
      label: b === -4 ? t.below(fmt.moneyShort(-3 * STEP)) : b === 3 ? t.above(fmt.moneyShort(3 * STEP)) : fmt.moneyShort(b * STEP),
      value: (counts.get(b) ?? 0) * (b < 0 ? -1 : 1),
    }));
  }, [trades, t, fmt]);

  const bigSize = sizes.length > 1 ? sizes[sizes.length - 1] : undefined;
  const sizeBad = bigSize ? bigSize.s.expectancy < s.expectancy : false;
  const m = t.m;

  const insights = compact([
    r.consistency > 0 && insight(
      r.consistency > 0.4 ? "bad" : "good",
      t.insightConsistency(fmt.pct(r.consistency)) + (r.consistency > 0.4 ? t.insightConsistencyBad : t.insightConsistencyGood),
    ),
    insight("info", t.insightVar(
      fmt.money(r.var95, { sign: false }),
      fmt.money(r.maxDrawdown),
      fmt.num(Math.abs(r.maxDrawdown) / Math.max(1, Math.abs(r.avgDaily)), 1),
    )),
    bigSize && insight(
      sizeBad ? "bad" : "good",
      t.insightSize(bigSize.label, fmt.money(bigSize.s.expectancy), fmt.money(s.expectancy)) + (sizeBad ? t.insightSizeBad : t.insightSizeGood),
    ),
  ]);

  return (
    <>
      <Insights items={insights} />
      <MetricGrid
        title={t.indicators}
        metrics={[
          { label: m.maxDd, value: fmt.money(r.maxDrawdown), tone: "loss", hint: m.maxDdHint },
          { label: m.recovery, value: fmt.num(r.recoveryFactor), hint: m.recoveryHint },
          { label: m.sharpe, value: fmt.num(r.sharpe), hint: m.sharpeHint },
          { label: m.sortino, value: fmt.num(r.sortino), hint: m.sortinoHint },
          { label: m.consistency, value: fmt.pct(r.consistency), tone: r.consistency > 0.4 ? "loss" : "gain", hint: m.consistencyHint },
          { label: m.var, value: fmt.money(r.var95), tone: "loss", hint: m.varHint },
          { label: m.std, value: fmt.money(r.dailyStd, { sign: false }), hint: m.stdHint },
          { label: m.worstDay, value: fmt.money(r.worstDay), tone: "loss" },
          { label: m.worst, value: fmt.money(s.worst), tone: "loss" },
          { label: m.maxLossStreak, value: st.maxLoss },
          { label: m.lossDays, value: r.lossDays, hint: m.lossDaysHint(r.winDays + r.lossDays) },
          { label: m.avgLoss, value: fmt.money(-s.avgLoss), tone: "loss" },
        ]}
      />
      <div className="grid xl:grid-cols-12 gap-5">
        <Card className="xl:col-span-8" title={t.drawdown} subtitle={t.drawdownSubtitle}>
          <UnderwaterChart data={curve} height={260} />
        </Card>
        <Card className="xl:col-span-4" title={t.distribution} subtitle={t.distributionSubtitle}>
          <PnlBars data={dailyDist} height={260} />
        </Card>
      </div>
      <div className="grid xl:grid-cols-12 gap-5">
        <Card className="xl:col-span-7" title={t.scatter} subtitle={t.scatterSubtitle}>
          <TradeScatter data={scatter} height={260} />
        </Card>
        <BucketTable className="xl:col-span-5" title={t.bySize} rows={sizes} />
      </div>
    </>
  );
}
